<!--
  弹窗字段组：form 16px / detail 12px 行距；列数 = min(字段数, 档位上限, cols)；
  传 force-cols 时按 cols 固定列数（少字段也占满栅格，便于与上下行对齐）。
  label 宽度取组内最宽（ResizeObserver 测量）。
  单字段跨列用 UiDialogField 的 span / full（full 不计入字段数）。
-->
<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

defineOptions({ name: "UiDialogFields" });

const props = withDefaults(
    defineProps<{
        /** form：表单间距 16；detail：详情间距 12 */
        variant?: "form" | "detail";
        /** 显式列数（不超过宿主弹窗 --dialog-cols-max） */
        cols?: number;
        /**
         * 强制使用 cols 列，不随字段数收缩。
         * 用于「仅 1～2 个字段也要按 4 列栅格占位 / 跨列」，与上下行对齐。
         */
        forceCols?: boolean;
    }>(),
    {
        variant: "detail",
        cols: undefined,
        forceCols: false,
    },
);

const rootRef = ref<HTMLElement | null>(null);
const resolvedCols = ref(1);

let resizeObserver: ResizeObserver | null = null;
let mutationObserver: MutationObserver | null = null;
let measureRaf = 0;

function readHostColsMax(el: HTMLElement): number {
    const host = el.closest(".el-dialog") ?? el.closest(".el-drawer") ?? el;
    const raw = getComputedStyle(host).getPropertyValue("--dialog-cols-max").trim();
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
}

function countLayoutFields(el: HTMLElement): number {
    const fields = el.querySelectorAll<HTMLElement>(":scope > [data-ui-dialog-field]");
    let count = 0;
    fields.forEach(field => {
        if (field.classList.contains("ui-dialog-field--full")) return;
        count += 1;
    });
    return count;
}

function measureLabelWidth(el: HTMLElement) {
    const labels = el.querySelectorAll<HTMLElement>("[data-ui-dialog-field-label]");
    let max = 0;
    labels.forEach(label => {
        // 业务侧 label-width 固定宽的字段不参与组内测量，避免抬高其它字段
        if (label.closest("[data-label-width-fixed='1']")) return;
        // 临时解除固定宽以测量真实文案宽度
        const prev = label.style.width;
        const prevFlex = label.style.flex;
        label.style.width = "auto";
        label.style.flex = "0 0 auto";
        max = Math.max(max, Math.ceil(label.getBoundingClientRect().width));
        label.style.width = prev;
        label.style.flex = prevFlex;
    });
    if (max > 0) {
        el.style.setProperty("--dialog-label-width", `${max}px`);
    }
}

function scheduleMeasure() {
    cancelAnimationFrame(measureRaf);
    measureRaf = requestAnimationFrame(() => {
        const el = rootRef.value;
        if (!el) return;
        const hostMax = readHostColsMax(el);
        const fieldCount = countLayoutFields(el);
        const explicit = props.cols != null && props.cols > 0 ? Math.floor(props.cols) : hostMax;
        const nextCols = props.forceCols ? Math.max(1, Math.min(hostMax, explicit)) : Math.max(1, Math.min(fieldCount || 1, hostMax, explicit));
        resolvedCols.value = nextCols;
        measureLabelWidth(el);
    });
}

const rootClass = computed(() => (props.variant === "form" ? "ui-dialog-form" : "ui-dialog-detail"));

onMounted(() => {
    scheduleMeasure();
    const el = rootRef.value;
    if (!el) return;
    if (typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver(() => scheduleMeasure());
        resizeObserver.observe(el);
        const host = el.closest(".el-dialog") ?? el.closest(".el-drawer");
        if (host) resizeObserver.observe(host);
    }
    if (typeof MutationObserver !== "undefined") {
        mutationObserver = new MutationObserver(() => scheduleMeasure());
        mutationObserver.observe(el, { childList: true, subtree: true, characterData: true });
    }
});

onBeforeUnmount(() => {
    cancelAnimationFrame(measureRaf);
    resizeObserver?.disconnect();
    resizeObserver = null;
    mutationObserver?.disconnect();
    mutationObserver = null;
});

watch(
    () => [props.variant, props.cols, props.forceCols] as const,
    async () => {
        await nextTick();
        scheduleMeasure();
    },
);

defineExpose({ scheduleMeasure });
</script>

<template>
    <div ref="rootRef" :class="rootClass" :data-cols="resolvedCols">
        <slot />
    </div>
</template>
